/**
 * The WRITE side of the state model `QueryBoundary` already runs on reads (#877).
 *
 * The asymmetry this closes, measured on `main`: a read that is refused says so
 * — `QueryBoundary.classifyError` calls 401/403 `unauthorized`, renders "You
 * don't have access to this" and deliberately offers NO retry. A write that is
 * refused said nothing at all. `packages/sdk/src/transport.ts` throws a plain
 * string `Error`, nothing caught it, `apps/console/src/data/client.tsx`
 * configured `queries` only, and the three generic write paths — the record
 * form, the list's inline cell edit, and the relations band — each swallowed it
 * differently:
 *
 *   · the form left the spinner stopped and the "Unsaved changes" bar up, with
 *     an unhandled promise rejection in the console and nothing on screen;
 *   · the list flashed the cell GREEN, unconditionally, and reverted silently on
 *     the next refetch — a UI asserting a write that never happened;
 *   · the relations band did nothing whatsoever.
 *
 * So: reads explained themselves and writes lied. This file is the write half,
 * and it MIRRORS the read half rather than inventing a second vocabulary —
 * `classifyWrite` delegates to `classifyError`, so a 403 is the same `kind` on
 * both sides and the two can never drift into disagreeing about what a 403 is.
 *
 * It also carries the question that comes BEFORE the failure: `writeDoorOf` —
 * whether this entity can be written directly at all. An `rpc` door is not a
 * permission the current user lacks, it is a door that is shut for everyone, and
 * a Save button over one is a button that cannot work.
 */
import { isWriteDoorClosed } from '@solum/sdk'
import { classifyError, type QueryStateKind } from './QueryBoundary'

/**
 * The read side's failure classes, plus ONE write-only class: `door-closed`,
 * the SDK's local #1131 refusal for an `rpc` write door. It cannot reuse
 * `unauthorized` — that template says an administrator can grant access, which
 * is exactly the implication a shut door forbids (no grant makes direct DML a
 * route) — and it cannot be `error`, whose retry affordance promises a
 * different answer the door will never give.
 */
export type WriteFailureKind = Exclude<QueryStateKind, 'loading' | 'ready'> | 'door-closed'

export interface WriteFailure {
  kind: WriteFailureKind
  /**
   * Whether pressing the button again could produce a different answer.
   *
   * False for `unauthorized` and `not-installed` — the same request is refused
   * the same way, and a retry affordance over one is the read side's own lesson
   * ("offering a button that cannot work") repeated on the write side.
   */
  retryable: boolean
  title: string
  message: string
}

/**
 * A message a user can act on, without leaking internals — the same trade
 * `QueryBoundary.messageFor` makes. The raw transport error carries URLs, schema
 * names and SQL; the status code survives because it is what support asks for.
 *
 * @param what a noun phrase naming the thing that was NOT saved, e.g. "this
 *             order". Optional: a generic sentence is better than a wrong one.
 */
export function classifyWrite(error: unknown, what?: string): WriteFailure {
  const it = what ? ` ${what}` : ' this'
  // Checked BEFORE classifyError: the door refusal is thrown locally by the
  // SDK, carries no status code, and is about the RECORD, not the person —
  // `writeDoorReason`'s sentence, at the moment of the attempt.
  if (isWriteDoorClosed(error)) {
    const subject = what ? what.charAt(0).toUpperCase() + what.slice(1) : 'This record'
    return {
      kind: 'door-closed', retryable: false,
      title: 'That change wasn’t saved',
      message: `${subject} is changed through its own actions, not by editing fields directly — nothing was saved. Use the actions on its surface to make the change.`,
    }
  }
  const kind = classifyError(error)
  switch (kind) {
    case 'unauthorized':
      return {
        kind, retryable: false,
        title: 'That change wasn’t saved',
        message: `Your account is not permitted to change${it}. Nothing was saved. An administrator can grant access in People & Access.`,
      }
    case 'not-installed':
      return {
        kind, retryable: false,
        title: 'That change wasn’t saved',
        message: `This deployment does not have the module that provides${it}, so there was nothing to save it to.`,
      }
    case 'offline':
      return {
        kind, retryable: true,
        title: 'That change wasn’t saved',
        message: 'The server could not be reached, so nothing was saved. Check your connection and try again.',
      }
    case 'error': {
      const msg = error instanceof Error ? error.message : String(error ?? '')
      const status = /\b(\d{3})\b/.exec(msg)?.[1]
      return {
        kind, retryable: true,
        title: 'That change wasn’t saved',
        message: status
          ? `The server returned an error (${status}) and nothing was saved. Try again — if it keeps happening, quote this code to support.`
          : 'Something went wrong and nothing was saved. Try again.',
      }
    }
  }
}

/* ── the report channel ─────────────────────────────────────────────────────
 *
 * The mutation cache lives at module scope, outside React, so it cannot call
 * `useToast()`. One slot, registered by a component INSIDE <ToastProvider> (see
 * `WriteFailureToaster`), keeps the wiring in one place instead of giving the
 * data layer its own parallel notification channel.
 *
 * Unset is a legitimate state — unit tests, SSR, the moment before the app
 * mounts — and a report with no sink is DROPPED rather than thrown. A failure to
 * announce a failure must not itself become the failure.
 */
let sink: ((failure: WriteFailure) => void) | null = null

export function setWriteFailureSink(fn: ((failure: WriteFailure) => void) | null): void {
  sink = fn
}

/** Announce a refused/failed write. Safe to call with no sink registered. */
export function reportWriteFailure(error: unknown, what?: string): WriteFailure {
  const failure = classifyWrite(error, what)
  sink?.(failure)
  return failure
}

/**
 * What the global net knows about the mutation that just failed — the shape
 * `MutationCache.onError` hands over, narrowed to what the decision needs.
 */
export interface FailedMutation {
  options?: { onError?: unknown }
  meta?: Record<string, unknown>
}

/**
 * Whether the LAST-RESORT net should speak for this failure.
 *
 * The net exists because a per-shape fix only covers the shapes somebody
 * enumerated, and #556 is about to flip more write doors — each flip turns a
 * working button into a silent one unless something catches the general case.
 * But the console already has ~18 call sites that report their own mutation
 * failures, and two toasts saying the same thing badly is its own defect.
 *
 * Two opt-outs, because there are two ways a surface handles its own errors and
 * only one of them is visible from here:
 *
 *   · `useMutation({ onError })` — the callback lands on `mutation.options`, so
 *     it is DETECTED, and nothing has to be annotated;
 *   · `mutate(vars, { onError })` — the callback lives on the observer, never on
 *     the mutation, so it is invisible at this layer. Those hooks declare
 *     `meta: { errorHandled: true }` at their DEFINITION, which covers every
 *     call site at once.
 *
 * Default is to REPORT. A new mutation that forgets to say anything gets the
 * generic sentence, which is the direction that fails safe: a duplicate is
 * noise, and silence is the bug.
 */
export function shouldReportWriteFailure(mutation: FailedMutation | undefined): boolean {
  if (!mutation) return true
  if (typeof mutation.options?.onError === 'function') return false
  return mutation.meta?.errorHandled !== true
}

/** The noun a mutation wants in its failure message, if it declared one. */
export function writeFailureSubject(mutation: FailedMutation | undefined): string | undefined {
  const what = mutation?.meta?.what
  return typeof what === 'string' ? what : undefined
}

/**
 * Do the write, and run the CONFIRMATION only if the server took it.
 *
 * The one sentence both generic write paths were missing, in the one place, so
 * they cannot drift into disagreeing about it:
 *
 *   · the list's inline cell edit confirmed with a green flash — `void
 *     save.mutateAsync(...)` discarded the promise, so the flash was
 *     unconditional and a 403 was confirmed as a success;
 *   · the record form confirmed by `reset(values)`, which clears the dirty state
 *     and takes the "Unsaved changes" bar down. That one happened to be correct
 *     on `main` — but only because the rejection PROPAGATED, as an unhandled
 *     promise rejection nothing was waiting for, which is the same defect
 *     wearing the other mask.
 *
 * Extracted rather than inlined because neither call site can be driven from a
 * test in this package: there is no DOM here (see `ListView.test.tsx`), so a
 * cell cannot be clicked and a form cannot be submitted. The decision that
 * matters — confirm iff resolved — is directly assertable here, and it is the
 * decision whose absence was the live bug.
 *
 * The failure is NOT reported here. It reaches the user through the mutation
 * cache net in `data/client.tsx` (and, on the form, through `save.error`);
 * catching a second time to announce a second time turns one refusal into two.
 * The catch exists so the rejection is HANDLED at all.
 *
 * @returns whether the server took it.
 */
export async function confirmedWrite(
  write: () => Promise<unknown>,
  onWritten: () => void,
): Promise<boolean> {
  try {
    await write()
  } catch {
    return false      // announced elsewhere; never confirmed here
  }
  onWritten()
  return true
}

/* ── the door, read from the metamodel ──────────────────────────────────────*/

export type WriteDoor = 'direct' | 'rpc'

/**
 * Where writes to this entity have to go, per the registry.
 *
 * ABSENT ⇒ `direct`, deliberately. `core.register_entity` resolves an omitted
 * `p_write_door` to `direct` (core 2026080319), and a deployment older than the
 * column sends nothing at all — reading either as "unknown, so refuse" would
 * turn every pre-0.151 box read-only, which is a far worse failure than the one
 * being fixed.
 */
export function writeDoorOf(
  // Structural, not `Pick<SchemaDescription, 'entities'>`: the only fields this
  // reads are `key` and `write_door`, and demanding the full `EntityTypeInfo`
  // would force every fixture and test rig to satisfy a shape the answer does
  // not depend on. `SchemaDescription` satisfies it.
  schema: { entities?: readonly { key: string; write_door?: string | null }[] } | null | undefined,
  entityKey: string,
): WriteDoor {
  const e = (schema?.entities ?? []).find((x) => x.key === entityKey)
  return e?.write_door === 'rpc' ? 'rpc' : 'direct'
}

/**
 * Why the generic editor will not offer a Save, in the user's words.
 *
 * Said as a property of the RECORD, not of the person: "you can't" invites them
 * to ask for a permission that would not help, because `enforce_write_doors`
 * revoked the DML for every authenticated role at once. What they need is the
 * module's own action, which is on the module's own surface.
 */
export function writeDoorReason(label: string): string {
  return `${label} records are changed through their own actions, not by editing fields directly — so this form is read-only. Use the actions on the ${label.toLowerCase()} surface to make a change.`
}

/**
 * The record is visible and this caller may not change it (#2031 prerequisite,
 * ruled in mailbox #1958 comment 62517).
 *
 * A DIFFERENT sentence from `writeDoorReason`, and the distinction is the point.
 * That one is a property of the ENTITY — nobody edits these fields directly —
 * and it sends the reader to the entity's own actions. This one is a property
 * of the PRINCIPAL: the form is editable for somebody, just not for you. Saying
 * the first when the second is true sends a person hunting for an action that
 * would refuse them too.
 */
export function capabilityRefusedReason(label: string): string {
  return `You can see this ${label.toLowerCase()} but you do not have permission to change it, so this form is read-only. Ask somebody who owns it, or who holds the role that manages ${label.toLowerCase()} records.`
}

/**
 * The capability could not be read — still in flight, or the door raised.
 *
 * Read-only, because the ruling is that a capability which is not positively
 * true fails CLOSED. Worded as an inability to ask rather than as a refusal:
 * telling somebody "you do not have permission" when the truth is "I could not
 * find out" is a false statement about them, and it is the one they would
 * repeat to whoever they ask for access.
 */
export function capabilityUnknownReason(label: string): string {
  return `This ${label.toLowerCase()} is read-only because your permission to change it could not be checked just now. Reload to try again — nothing has been changed.`
}
